import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CheckCircle, XCircle, Clock, Loader2, Play, ChevronDown,
  ChevronUp, Zap, ArrowLeft, RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const METHOD_COLORS = {
  GET: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  POST: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  PUT: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  PATCH: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  DELETE: "bg-red-500/20 text-red-400 border-red-500/30",
};

function statusColor(code) {
  if (!code) return "text-slate-400";
  if (code < 300) return "text-emerald-400";
  if (code < 400) return "text-amber-400";
  return "text-red-400";
}

function formatDuration(start, end) {
  if (!start || !end) return null;
  const ms = new Date(end) - new Date(start);
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function StepResult({ step, index }) {
  const [expanded, setExpanded] = useState(true);
  const passed = step.passed;
  const failedAssertions = (step.assertions || []).filter(a => !a.passed);

  return (
    <Card className={`border ${passed ? "border-emerald-500/20" : "border-red-500/20"} glass-card`}>
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
        onClick={() => setExpanded(v => !v)}
      >
        {passed
          ? <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          : <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />}
        <div className="w-6 h-6 bg-white/5 rounded-sm flex items-center justify-center flex-shrink-0">
          <span className="text-slate-400 text-xs font-bold">{index + 1}</span>
        </div>
        <Badge className={`text-xs font-mono border flex-shrink-0 ${METHOD_COLORS[step.method] || METHOD_COLORS.GET}`}>
          {step.method}
        </Badge>
        <span className="text-slate-300 text-sm font-mono flex-1 truncate">{step.url}</span>
        {step.status_code && (
          <span className={`text-sm font-mono font-bold flex-shrink-0 ${statusColor(step.status_code)}`}>
            {step.status_code}
          </span>
        )}
        {step.elapsed_ms != null && (
          <span className="text-slate-500 text-xs flex-shrink-0">{step.elapsed_ms}ms</span>
        )}
        {!passed && failedAssertions.length > 0 && (
          <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-xs flex-shrink-0">
            {failedAssertions.length} failed
          </Badge>
        )}
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-500 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />}
      </div>

      {/* Body */}
      {expanded && (
        <CardContent className="pt-0 space-y-4 border-t border-white/5">
          {step.error && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
              <p className="text-red-400 text-sm font-mono">{step.error}</p>
            </div>
          )}

          {/* Step name */}
          {step.name && (
            <p className="text-slate-400 text-sm mt-4">{step.name}</p>
          )}

          {/* Assertions */}
          {step.assertions && step.assertions.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Assertions</p>
              <div className="space-y-1">
                {step.assertions.map((a, i) => (
                  <div key={i} className={`flex items-start gap-2 p-2 rounded-sm text-sm ${a.passed ? "bg-emerald-500/5" : "bg-red-500/5"}`}>
                    {a.passed
                      ? <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                      : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />}
                    <span className={a.passed ? "text-emerald-300" : "text-red-300"}>{a.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Extracted variables */}
          {step.extracted && Object.keys(step.extracted).length > 0 && (
            <div>
              <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Extracted Variables</p>
              <div className="space-y-1">
                {Object.entries(step.extracted).map(([key, val]) => (
                  <div key={key} className="flex gap-2 text-sm">
                    <span className="text-purple-400 font-mono">{`{{${key}}}`}</span>
                    <span className="text-slate-500">=</span>
                    <span className="text-slate-300 font-mono truncate">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Response body */}
          {step.response_body && (
            <div>
              <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Response Body</p>
              <pre className="bg-black/40 border border-white/5 rounded-sm p-3 text-xs text-slate-300 font-mono overflow-x-auto max-h-48 overflow-y-auto">
                {(() => {
                  try { return JSON.stringify(JSON.parse(step.response_body), null, 2); }
                  catch { return step.response_body; }
                })()}
              </pre>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default function APIExecutionDetails() {
  const { executionId } = useParams();
  const navigate = useNavigate();
  const [execution, setExecution] = useState(null);
  const [testCase, setTestCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rerunning, setRerunning] = useState(false);

  const fetchExecution = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/executions/${executionId}`, { withCredentials: true });
      setExecution(res.data);
      if (res.data.test_case_id && !testCase) {
        const tcRes = await axios.get(`${API}/test-cases/${res.data.test_case_id}`, { withCredentials: true });
        setTestCase(tcRes.data);
      }
    } catch {
      toast.error("Failed to load execution");
    } finally {
      setLoading(false);
    }
  }, [executionId, testCase]);

  useEffect(() => {
    fetchExecution();
  }, [fetchExecution]);

  // Poll while running
  useEffect(() => {
    if (!execution) return;
    if (execution.status === "running" || execution.status === "queued") {
      const timer = setTimeout(fetchExecution, 2000);
      return () => clearTimeout(timer);
    }
  }, [execution, fetchExecution]);

  const handleRerun = async () => {
    if (!execution) return;
    setRerunning(true);
    try {
      const res = await axios.post(
        `${API}/executions?test_case_id=${execution.test_case_id}`,
        {},
        { withCredentials: true }
      );
      toast.success("Rerunning API test...");
      navigate(`/api-execution/${res.data.id}`);
    } catch {
      toast.error("Failed to rerun");
    } finally {
      setRerunning(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
    </div>
  );

  const isRunning = execution?.status === "running" || execution?.status === "queued";
  const stepResults = execution?.api_step_results || [];
  const passed = execution?.status === "completed";
  const duration = formatDuration(execution?.start_time, execution?.end_time);

  const passedSteps = stepResults.filter(s => s.passed).length;
  const failedSteps = stepResults.filter(s => !s.passed).length;

  return (
    <div className="min-h-screen bg-black text-white">
      <AppNav />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>

          {/* Back */}
          <Button variant="ghost" className="text-slate-400 hover:text-white mb-6 -ml-2"
            onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>

          {/* Status Header */}
          <div className="flex items-start justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${isRunning ? "bg-blue-500/20" : passed ? "bg-emerald-500/20" : "bg-red-500/20"}`}>
                {isRunning
                  ? <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                  : passed
                    ? <CheckCircle className="w-6 h-6 text-emerald-400" />
                    : <XCircle className="w-6 h-6 text-red-400" />}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-blue-400" />
                  <span className="text-slate-400 text-sm">API Test</span>
                  <Badge className={`text-xs ${isRunning ? "bg-blue-500/20 text-blue-400" : passed ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                    {isRunning ? "Running..." : passed ? "PASSED" : "FAILED"}
                  </Badge>
                </div>
                <h1 className="text-xl font-bold">{testCase?.name || "API Test Execution"}</h1>
                <div className="flex items-center gap-4 mt-1 text-slate-500 text-sm">
                  {duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{duration}</span>}
                  {stepResults.length > 0 && (
                    <span>{passedSteps}/{stepResults.length} steps passed</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" className="text-slate-400" onClick={fetchExecution}>
                <RefreshCw className="w-4 h-4 mr-1" /> Refresh
              </Button>
              <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm"
                onClick={handleRerun} disabled={rerunning || isRunning}>
                {rerunning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                Rerun
              </Button>
            </div>
          </div>

          {/* Summary bar */}
          {stepResults.length > 0 && (
            <div className="grid grid-cols-3 gap-4 mb-8">
              <Card className="glass-card border-white/10">
                <CardContent className="pt-4 pb-4 text-center">
                  <p className="text-2xl font-bold text-emerald-400">{passedSteps}</p>
                  <p className="text-slate-400 text-xs mt-1">Passed</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-white/10">
                <CardContent className="pt-4 pb-4 text-center">
                  <p className="text-2xl font-bold text-red-400">{failedSteps}</p>
                  <p className="text-slate-400 text-xs mt-1">Failed</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-white/10">
                <CardContent className="pt-4 pb-4 text-center">
                  <p className="text-2xl font-bold text-slate-300">
                    {stepResults.reduce((acc, s) => acc + (s.elapsed_ms || 0), 0).toFixed(0)}ms
                  </p>
                  <p className="text-slate-400 text-xs mt-1">Total Time</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Running state */}
          {isRunning && stepResults.length === 0 && (
            <Card className="glass-card border-blue-500/20 mb-6">
              <CardContent className="py-12 text-center">
                <Loader2 className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-3" />
                <p className="text-slate-300">Executing API requests...</p>
                <p className="text-slate-500 text-sm mt-1">Results will appear here automatically</p>
              </CardContent>
            </Card>
          )}

          {/* Step Results */}
          {stepResults.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-200">Step Results</h2>
              {stepResults.map((step, idx) => (
                <StepResult key={idx} step={step} index={idx} />
              ))}
            </div>
          )}

          {/* Result summary */}
          {execution?.result && (
            <div className={`mt-6 p-4 rounded-sm border ${passed ? "bg-emerald-500/5 border-emerald-500/20" : "bg-red-500/5 border-red-500/20"}`}>
              <p className={`text-sm ${passed ? "text-emerald-300" : "text-red-300"}`}>{execution.result}</p>
            </div>
          )}

          {/* Edit button */}
          {testCase && (
            <div className="mt-6 flex justify-end">
              <Button variant="outline" className="border-white/10 text-slate-300 hover:text-white rounded-sm"
                onClick={() => navigate(`/edit-api-test/${testCase.id}`)}>
                Edit API Test
              </Button>
            </div>
          )}

        </motion.div>
      </div>
    </div>
  );
}
